import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import {
  ArrowLeft,
  Users,
  UserPlus,
  Mail,
  MoreVertical,
  Clock,
  Menu,
  Crown,
  Lock,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { useAuth, usePermissions } from '@/contexts/AuthContext';
import { useChatLayout } from '@/components/layout/ChatLayout';
import WorkspacePill from '@/components/layout/WorkspacePill';
import { teamApi } from '@/services/team-api';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import DataAccessDrawer from '@/components/settings/DataAccessDrawer';
import DeletionImpactPreview, { type ImpactItem } from '@/components/settings/DeletionImpactPreview';
import type { OrgUser, Invitation, WorkspaceRole, AssignableWorkspaceRole, WorkspaceTier } from '@/types';

/** Pooled seat entitlement for the active workspace (drives the over-limit invite notice). */
interface SeatInfo {
  seatLimit: number;
  seatsUsed: number;
  seatsIncluded: number;
  seatAddonPriceUsd: number | null;
  addonAvailable: boolean;
  tier: WorkspaceTier | null;
}

const ASSIGNABLE_ROLES: { value: AssignableWorkspaceRole; label: string }[] = [
  { value: 'workspace-admin', label: 'Admin' },
  { value: 'workspace-member', label: 'Member' },
];

const ROLE_LABELS: Record<WorkspaceRole, string> = {
  'workspace-owner': 'Owner',
  'workspace-admin': 'Admin',
  'workspace-member': 'Member',
};

const ROLE_COLORS: Record<WorkspaceRole, string> = {
  'workspace-owner': 'bg-warn/15 text-warn',
  'workspace-admin': 'bg-accent text-primary',
  'workspace-member': 'bg-muted text-foreground',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-success/15 text-success',
  suspended: 'bg-destructive/15 text-destructive',
  pending: 'bg-muted text-muted-foreground',
};

function formatDate(d: string | null): string {
  if (!d) return 'Never';
  return new Date(d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Mirrors the backend (UserManagementService): an owner may assign admin or
// member; an admin may assign only member. Owner is never directly assignable.
function getAssignableRoles(callerRole: WorkspaceRole | null): AssignableWorkspaceRole[] {
  if (callerRole === 'workspace-owner') return ['workspace-admin', 'workspace-member'];
  if (callerRole === 'workspace-admin') return ['workspace-member'];
  return [];
}

function fullName(m: { firstName: string; lastName: string | null }): string {
  return `${m.firstName}${m.lastName ? ` ${m.lastName}` : ''}`;
}

const TeamPage: React.FC = () => {
  const { user } = useAuth();
  const { canManageUsers, isOwner, workspaceRole } = usePermissions();
  const { isMobile, toggleSidebar } = useChatLayout();
  const queryClient = useQueryClient();

  // State
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [actionMenuUserId, setActionMenuUserId] = useState<string | null>(null);
  const [roleChangeUser, setRoleChangeUser] = useState<OrgUser | null>(null);
  const [dataAccessUser, setDataAccessUser] = useState<OrgUser | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'suspend' | 'reactivate' | 'remove' | 'forceSignOut' | 'revokeInvitation';
    target: OrgUser | Invitation;
    label: string;
  } | null>(null);
  const [transferUser, setTransferUser] = useState<OrgUser | null>(null);
  const [transferPassword, setTransferPassword] = useState('');
  const [tab, setTab] = useState<'members' | 'invitations'>('members');

  // Queries
  const usersQuery = useQuery({
    queryKey: ['orgUsers'],
    queryFn: teamApi.listUsers,
  });

  const invitationsQuery = useQuery({
    queryKey: ['orgInvitations'],
    queryFn: () => teamApi.listInvitations('pending'),
    enabled: canManageUsers(),
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['orgUsers'] });
    queryClient.invalidateQueries({ queryKey: ['orgInvitations'] });
  };

  // Mutations
  const changeRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: AssignableWorkspaceRole }) =>
      teamApi.changeRole(userId, role),
    onSuccess: () => {
      toast.success('Role updated');
      invalidateAll();
      setRoleChangeUser(null);
    },
  });

  const suspendMutation = useMutation({
    mutationFn: (userId: string) => teamApi.suspendUser(userId),
    onSuccess: () => { toast.success('User suspended'); invalidateAll(); setConfirmAction(null); },
  });

  const reactivateMutation = useMutation({
    mutationFn: (userId: string) => teamApi.reactivateUser(userId),
    onSuccess: () => { toast.success('User reactivated'); invalidateAll(); setConfirmAction(null); },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => teamApi.removeUser(userId),
    onSuccess: () => { toast.success('User removed'); invalidateAll(); setConfirmAction(null); },
  });

  const forceSignOutMutation = useMutation({
    mutationFn: (userId: string) => teamApi.forceSignOut(userId),
    onSuccess: () => { toast.success('All sessions revoked'); setConfirmAction(null); },
  });

  const revokeInvitationMutation = useMutation({
    mutationFn: (id: string) => teamApi.revokeInvitation(id),
    onSuccess: () => { toast.success('Invitation revoked'); invalidateAll(); setConfirmAction(null); },
  });

  const transferMutation = useMutation({
    mutationFn: ({ targetUserId, password }: { targetUserId: string; password: string }) =>
      teamApi.transferOwnership(targetUserId, password),
    onSuccess: () => {
      toast.success('Ownership transferred');
      invalidateAll();
      setTransferUser(null);
      setTransferPassword('');
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });

  const handleConfirm = () => {
    if (!confirmAction) return;
    const target = confirmAction.target;
    switch (confirmAction.type) {
      case 'suspend': suspendMutation.mutate((target as OrgUser).id); break;
      case 'reactivate': reactivateMutation.mutate((target as OrgUser).id); break;
      case 'remove': removeMutation.mutate((target as OrgUser).id); break;
      case 'forceSignOut': forceSignOutMutation.mutate((target as OrgUser).id); break;
      case 'revokeInvitation': revokeInvitationMutation.mutate((target as Invitation).id); break;
    }
  };

  const isConfirmLoading =
    suspendMutation.isPending || reactivateMutation.isPending ||
    removeMutation.isPending || forceSignOutMutation.isPending ||
    revokeInvitationMutation.isPending;

  const users = usersQuery.data?.users ?? [];
  const invitations = invitationsQuery.data?.invitations ?? [];
  const seatLimit = usersQuery.data?.seatLimit ?? 0;
  const seatsUsed = usersQuery.data?.seatsUsed ?? 0;
  const seatInfo: SeatInfo = {
    seatLimit,
    seatsUsed,
    seatsIncluded: usersQuery.data?.seatsIncluded ?? seatLimit,
    seatAddonPriceUsd: usersQuery.data?.seatAddonPriceUsd ?? null,
    addonAvailable: usersQuery.data?.addonAvailable ?? false,
    tier: usersQuery.data?.tier ?? null,
  };

  // Reactivating a suspended member re-consumes a seat (backend Model A), so it's
  // gated by the same pooled limit as an invite. Pre-block here to mirror the
  // invite modal; the server enforces it regardless (402/403).
  const atSeatLimit = seatLimit > 0 && seatsUsed >= seatLimit;
  const reactivateBlocked = confirmAction?.type === 'reactivate' && atSeatLimit;

  // Impact preview for the remove confirm (P1). Only fetched for a 'remove' action.
  const removeTarget = confirmAction?.type === 'remove' ? (confirmAction.target as OrgUser) : null;
  const removeImpact = useQuery({
    queryKey: ['userDeletionImpact', removeTarget?.id],
    queryFn: () => teamApi.getDeletionImpact(removeTarget!.id),
    enabled: !!removeTarget,
  });
  const removeImpactItems: ImpactItem[] = removeImpact.data
    ? [
        { label: 'Sources they lose access to', value: removeImpact.data.sourcesWithAccess.length },
        {
          label: 'Sources left with no one (orphaned)',
          value: removeImpact.data.soleGranteeSources.length,
          emphasize: removeImpact.data.soleGranteeSources.length > 0,
        },
        { label: 'Queries in the last 30 days', value: removeImpact.data.queries30d },
      ]
    : [];

  if (usersQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button onClick={toggleSidebar} className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors" aria-label="Open sidebar">
          <Menu className="w-5 h-5" />
        </button>
      )}

      <div className="w-full max-w-4xl mx-auto space-y-6">
        {/* Header — back link left, workspace selector top-right (matches /home) */}
        <div className="flex items-center justify-between gap-4">
          <Link to="/settings" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Settings
          </Link>
          <WorkspacePill />
        </div>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Team Members</h1>
            <p className="text-muted-foreground mt-1">Manage your organization's team</p>
          </div>
          {canManageUsers() && (
            <Button leftIcon={<UserPlus className="w-4 h-4" />} onClick={() => setShowInviteModal(true)}>
              Invite User
            </Button>
          )}
        </div>

        {/* Seat Counter */}
        {seatLimit > 0 && (
          <div className="rounded-xl bg-muted border border-border p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">
                <Users className="w-4 h-4 inline mr-1.5" />
                {seatsUsed} of {seatLimit} seats used
              </span>
              <span className="text-xs text-muted-foreground">
                {Math.max(0, seatLimit - seatsUsed)} remaining
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className={clsx(
                  'h-2 rounded-full transition-all',
                  seatsUsed / seatLimit > 0.9 ? 'bg-destructive' : 'bg-primary'
                )}
                style={{ width: `${Math.min((seatsUsed / seatLimit) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Tabs (only show invitations tab for admins) */}
        {canManageUsers() && (
          <div className="flex border-b border-border">
            <button
              onClick={() => setTab('members')}
              className={clsx(
                'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                tab === 'members'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              Members ({users.length})
            </button>
            <button
              onClick={() => setTab('invitations')}
              className={clsx(
                'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                tab === 'invitations'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              Pending Invitations ({invitations.length})
            </button>
          </div>
        )}

        {/* Members List */}
        {tab === 'members' && (
          <div className="rounded-xl bg-muted border border-border divide-y divide-border [&>*:first-child]:rounded-t-xl [&>*:last-child]:rounded-b-xl">
            {users.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No team members found.</div>
            ) : (
              users
                .sort((a, b) => {
                  const order: WorkspaceRole[] = ['workspace-owner', 'workspace-admin', 'workspace-member'];
                  return order.indexOf(a.role) - order.indexOf(b.role);
                })
                .map((u) => (
                  <UserRow
                    key={u.id}
                    member={u}
                    currentUser={user!}
                    canManage={canManageUsers()}
                    isOwner={isOwner}
                    isMenuOpen={actionMenuUserId === u.id}
                    onToggleMenu={() => setActionMenuUserId(actionMenuUserId === u.id ? null : u.id)}
                    onCloseMenu={() => setActionMenuUserId(null)}
                    onChangeRole={() => { setRoleChangeUser(u); setActionMenuUserId(null); }}
                    onManageAccess={() => { setDataAccessUser(u); setActionMenuUserId(null); }}
                    onSuspend={() => {
                      setConfirmAction({ type: 'suspend', target: u, label: `Suspend ${fullName(u)}?` });
                      setActionMenuUserId(null);
                    }}
                    onReactivate={() => {
                      setConfirmAction({ type: 'reactivate', target: u, label: `Reactivate ${fullName(u)}?` });
                      setActionMenuUserId(null);
                    }}
                    onRemove={() => {
                      setConfirmAction({ type: 'remove', target: u, label: `Remove ${fullName(u)} from this workspace? This cannot be undone.` });
                      setActionMenuUserId(null);
                    }}
                    onForceSignOut={() => {
                      setConfirmAction({ type: 'forceSignOut', target: u, label: `Force sign out all sessions for ${fullName(u)}?` });
                      setActionMenuUserId(null);
                    }}
                    onTransferOwnership={() => { setTransferUser(u); setActionMenuUserId(null); }}
                  />
                ))
            )}
          </div>
        )}

        {/* Invitations List */}
        {tab === 'invitations' && canManageUsers() && (
          <div className="rounded-xl bg-muted border border-border divide-y divide-border [&>*:first-child]:rounded-t-xl [&>*:last-child]:rounded-b-xl">
            {invitationsQuery.isLoading ? (
              <div className="p-8 flex justify-center"><LoadingSpinner /></div>
            ) : invitations.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No pending invitations.</div>
            ) : (
              invitations.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between p-4 sm:p-5">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{inv.email}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', ROLE_COLORS[inv.role])}>
                          {ROLE_LABELS[inv.role]}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Expires {formatDate(inv.expires_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive/80 hover:bg-destructive/15"
                    onClick={() => setConfirmAction({
                      type: 'revokeInvitation',
                      target: inv,
                      label: `Revoke invitation to ${inv.email}?`,
                    })}
                  >
                    Revoke
                  </Button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Invite Modal */}
      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        callerRole={workspaceRole}
        seatInfo={seatInfo}
        onSuccess={invalidateAll}
      />

      {/* Per-member data access */}
      <DataAccessDrawer
        isOpen={!!dataAccessUser}
        onClose={() => setDataAccessUser(null)}
        member={dataAccessUser ? { id: dataAccessUser.id, name: fullName(dataAccessUser), email: dataAccessUser.email } : null}
      />

      {/* Role Change Modal */}
      {roleChangeUser && (
        <RoleChangeModal
          user={roleChangeUser}
          callerRole={workspaceRole}
          isLoading={changeRoleMutation.isPending}
          onClose={() => setRoleChangeUser(null)}
          onSave={(role) => changeRoleMutation.mutate({ userId: roleChangeUser.id, role })}
        />
      )}

      {/* Confirm Action Modal */}
      {confirmAction && (
        <Modal isOpen onClose={() => setConfirmAction(null)} size="sm">
          <ModalHeader onClose={() => setConfirmAction(null)}>
            {confirmAction.type === 'remove' ? 'Remove User' :
             confirmAction.type === 'revokeInvitation' ? 'Revoke Invitation' :
             'Confirm Action'}
          </ModalHeader>
          <ModalBody>
            <p className="text-muted-foreground">{confirmAction.label}</p>
            {confirmAction.type === 'remove' && (
              <DeletionImpactPreview
                items={removeImpactItems}
                loading={removeImpact.isLoading}
                error={removeImpact.isError}
                title="What this workspace loses"
                className="mt-3"
              />
            )}
            {reactivateBlocked && (
              <div className="mt-4 rounded-lg bg-warn/15 border border-warn/20 p-3 space-y-2">
                <div className="flex gap-2">
                  <Info className="w-4 h-4 text-warn flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-warn">
                    Your {tierLabel(seatInfo.tier)} includes{' '}
                    <strong>{seatInfo.seatsIncluded} {seatInfo.seatsIncluded === 1 ? 'seat' : 'seats'}</strong>,
                    and {seatInfo.seatsIncluded === 1 ? "it's" : "they're"} all in use. Reactivating
                    this member needs a free seat.{' '}
                    {seatInfo.addonAvailable && seatInfo.seatAddonPriceUsd != null
                      ? <>Additional seats are <strong>${seatInfo.seatAddonPriceUsd}/mo</strong> each.</>
                      : <>Upgrade your plan to add more seats.</>}
                  </p>
                </div>
                <Link
                  to="/settings/billing"
                  className="inline-block pl-6 text-sm font-semibold text-primary hover:underline"
                >
                  {seatInfo.addonAvailable ? 'Add seats in Billing →' : 'Go to Billing →'}
                </Link>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)} disabled={isConfirmLoading}>
              Cancel
            </Button>
            <Button
              variant={confirmAction.type === 'remove' || confirmAction.type === 'revokeInvitation' ? 'destructive' : 'primary'}
              onClick={handleConfirm}
              isLoading={isConfirmLoading}
              disabled={reactivateBlocked}
            >
              Confirm
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Ownership Transfer Modal */}
      {transferUser && (
        <Modal isOpen onClose={() => { setTransferUser(null); setTransferPassword(''); }} size="sm" closeOnOverlayClick={false}>
          <ModalHeader onClose={() => { setTransferUser(null); setTransferPassword(''); }}>
            Transfer Ownership
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-warn/15 border border-warn/20">
                <div className="flex gap-2">
                  <AlertTriangle className="w-5 h-5 text-warn flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-warn">
                    You are about to transfer ownership of this workspace to <strong>{fullName(transferUser)}</strong>. You will be demoted to Admin. This cannot be undone without the new owner's consent.
                  </p>
                </div>
              </div>
              <Input
                label="Enter your password to confirm"
                type="password"
                leftIcon={<Lock className="w-4 h-4 text-muted-foreground" />}
                value={transferPassword}
                onChange={(e) => setTransferPassword(e.target.value)}
                placeholder="Your current password"
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => { setTransferUser(null); setTransferPassword(''); }} disabled={transferMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!transferPassword}
              isLoading={transferMutation.isPending}
              onClick={() => transferMutation.mutate({ targetUserId: transferUser.id, password: transferPassword })}
            >
              Transfer Ownership
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

// ─── User Row ────────────────────────────────────────────────────────────────

interface UserRowProps {
  member: OrgUser;
  currentUser: { id: string };
  canManage: boolean;
  isOwner: boolean;
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
  onChangeRole: () => void;
  onManageAccess: () => void;
  onSuspend: () => void;
  onReactivate: () => void;
  onRemove: () => void;
  onForceSignOut: () => void;
  onTransferOwnership: () => void;
}

const UserRow: React.FC<UserRowProps> = ({
  member, currentUser, canManage, isOwner,
  isMenuOpen, onToggleMenu, onCloseMenu,
  onChangeRole, onManageAccess, onSuspend, onReactivate, onRemove, onForceSignOut, onTransferOwnership,
}) => {
  const isSelf = member.id === currentUser.id;
  const isMemberOwner = member.role === 'workspace-owner';
  const showActions = canManage && !isSelf && !isMemberOwner;
  const showTransfer = isOwner && !isSelf && member.role === 'workspace-admin' && member.status === 'active';

  return (
    <div className="flex items-center justify-between p-4 sm:p-5 hover:bg-accent/30 dark:hover:bg-white/[0.02] transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-white text-sm font-medium">
          {member.firstName[0]}{member.lastName?.[0] ?? ''}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground truncate">
              {fullName(member)}
              {isSelf && <span className="text-xs text-muted-foreground ml-1">(you)</span>}
            </p>
          </div>
          <p className="text-xs text-muted-foreground truncate">{member.email}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', ROLE_COLORS[member.role])}>
              {member.role === 'workspace-owner' && <Crown className="w-3 h-3" />}
              {ROLE_LABELS[member.role]}
            </span>
            {member.status !== 'active' && (
              <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize', STATUS_COLORS[member.status])}>
                {member.status}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="hidden sm:block text-right">
          <p className="text-xs text-muted-foreground">
            Joined {formatDate(member.joinedAt)}
          </p>
          {member.lastLoginAt && (
            <p className="text-xs text-muted-foreground">
              Last login {formatDate(member.lastLoginAt)}
            </p>
          )}
        </div>

        {(showActions || showTransfer) && (
          <div className="relative">
            <button
              onClick={onToggleMenu}
              className="p-1.5 rounded-lg hover:bg-accent dark:hover:bg-white/10 transition-colors"
            >
              <MoreVertical className="w-4 h-4 text-muted-foreground" />
            </button>
            {isMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={onCloseMenu} />
                <div className="absolute right-0 mt-1 w-48 bg-card rounded-lg shadow-lg border border-border z-20 py-1">
                  {showActions && (
                    <>
                      <MenuButton onClick={onChangeRole}>Change Role</MenuButton>
                      <MenuButton onClick={onManageAccess}>Manage data access</MenuButton>
                      {member.status === 'active' && <MenuButton onClick={onSuspend}>Suspend</MenuButton>}
                      {member.status === 'suspended' && <MenuButton onClick={onReactivate}>Reactivate</MenuButton>}
                      <MenuButton onClick={onForceSignOut}>Force Sign Out</MenuButton>
                      <div className="border-t border-border my-1" />
                      <MenuButton onClick={onRemove} destructive>Remove from Org</MenuButton>
                    </>
                  )}
                  {showTransfer && (
                    <>
                      {showActions && <div className="border-t border-border my-1" />}
                      <MenuButton onClick={onTransferOwnership}>
                        <Crown className="w-3.5 h-3.5 mr-1.5 inline" />
                        Transfer Ownership
                      </MenuButton>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const MenuButton: React.FC<{ onClick: () => void; destructive?: boolean; children: React.ReactNode }> = ({
  onClick, destructive, children,
}) => (
  <button
    onClick={onClick}
    className={clsx(
      'w-full text-left px-3 py-2 text-sm transition-colors',
      destructive
        ? 'text-destructive hover:bg-destructive/15'
        : 'text-foreground hover:bg-accent'
    )}
  >
    {children}
  </button>
);

// ─── Invite Modal ────────────────────────────────────────────────────────────

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  callerRole: WorkspaceRole | null;
  seatInfo: SeatInfo;
  onSuccess: () => void;
}

function tierLabel(tier: WorkspaceTier | null): string {
  if (!tier) return 'plan';
  return `${tier.charAt(0).toUpperCase()}${tier.slice(1)} plan`;
}

const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose, callerRole, seatInfo, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AssignableWorkspaceRole>('workspace-member');

  const mutation = useMutation({
    mutationFn: () => teamApi.createInvitation(email, role),
    onSuccess: () => {
      toast.success(`Invitation sent to ${email}`);
      onSuccess();
      setEmail('');
      setRole('workspace-member');
      onClose();
    },
  });

  const assignable = getAssignableRoles(callerRole);

  // Over-limit (Option B): every included seat is in use. Show an inline notice
  // that points to Billing and block the send — seats are added there, not here.
  const atSeatLimit = seatInfo.seatLimit >= 0 && seatInfo.seatsUsed >= seatInfo.seatLimit;
  const seatWord = seatInfo.seatsIncluded === 1 ? 'seat' : 'seats';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalHeader onClose={onClose}>Invite Team Member</ModalHeader>
      <ModalBody>
        <div className="space-y-4">
          <Input
            label="Email Address"
            type="email"
            leftIcon={<Mail className="w-4 h-4 text-muted-foreground" />}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@company.com"
            disabled={atSeatLimit}
          />
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as AssignableWorkspaceRole)}
              disabled={atSeatLimit}
              className="block w-full rounded-lg text-sm bg-card border border-input text-foreground px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring/40 transition-colors disabled:opacity-50"
            >
              {ASSIGNABLE_ROLES.filter(r => assignable.includes(r.value)).map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {atSeatLimit && (
            <div className="rounded-lg bg-warn/15 border border-warn/20 p-3 space-y-2">
              <div className="flex gap-2">
                <Info className="w-4 h-4 text-warn flex-shrink-0 mt-0.5" />
                <p className="text-sm text-warn">
                  Your {tierLabel(seatInfo.tier)} includes{' '}
                  <strong>{seatInfo.seatsIncluded} {seatWord}</strong>, and{' '}
                  {seatInfo.seatsIncluded === 1 ? "it's" : "they're"} all in use.{' '}
                  {seatInfo.addonAvailable && seatInfo.seatAddonPriceUsd != null
                    ? <>Additional seats are <strong>${seatInfo.seatAddonPriceUsd}/mo</strong> each.</>
                    : <>Upgrade your plan to add more seats.</>}
                </p>
              </div>
              <Link
                to="/settings/billing"
                className="inline-block pl-6 text-sm font-semibold text-primary hover:underline"
              >
                {seatInfo.addonAvailable ? 'Add seats in Billing →' : 'Go to Billing →'}
              </Link>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
        <Button
          onClick={() => mutation.mutate()}
          isLoading={mutation.isPending}
          disabled={atSeatLimit || !email.includes('@')}
          leftIcon={<UserPlus className="w-4 h-4" />}
        >
          Send Invitation
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ─── Role Change Modal ──────────────────────────────────────────────────────

interface RoleChangeModalProps {
  user: OrgUser;
  callerRole: WorkspaceRole | null;
  isLoading: boolean;
  onClose: () => void;
  onSave: (role: AssignableWorkspaceRole) => void;
}

const RoleChangeModal: React.FC<RoleChangeModalProps> = ({ user: target, callerRole, isLoading, onClose, onSave }) => {
  // The owner role is never a change-role target, so default to the member's
  // current role when it's assignable, else the first assignable option.
  const initialRole: AssignableWorkspaceRole =
    target.role === 'workspace-admin' ? 'workspace-admin' : 'workspace-member';
  const [selectedRole, setSelectedRole] = useState<AssignableWorkspaceRole>(initialRole);
  const assignable = getAssignableRoles(callerRole);

  return (
    <Modal isOpen onClose={onClose} size="sm">
      <ModalHeader onClose={onClose}>Change Role</ModalHeader>
      <ModalBody>
        <p className="text-sm text-muted-foreground mb-4">
          Change role for <strong>{fullName(target)}</strong>
        </p>
        <div className="space-y-2">
          {ASSIGNABLE_ROLES.filter(r => assignable.includes(r.value)).map(r => (
            <label
              key={r.value}
              className={clsx(
                'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                selectedRole === r.value
                  ? 'border-primary bg-accent'
                  : 'border-border hover:bg-muted'
              )}
            >
              <input
                type="radio"
                name="role"
                value={r.value}
                checked={selectedRole === r.value}
                onChange={() => setSelectedRole(r.value)}
                className="text-primary focus:ring-ring"
              />
              <span className="text-sm font-medium text-foreground capitalize">{r.label}</span>
            </label>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button onClick={() => onSave(selectedRole)} isLoading={isLoading} disabled={selectedRole === target.role}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TeamPage;
